import { create } from 'zustand';
import { ref, onValue, get } from 'firebase/database';
import { db, auth } from '../../../integrations/firebase';
import { createAgentAccount } from '../../../integrations/firebase/secondaryApp';
import { authHeaders } from '../../../core/api/authHeaders';
import { isHrRole, withoutPrivate } from '../privateFields';

const PROXY_BASE = (import.meta.env.VITE_PROXY_URL || (import.meta.env.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');

// The proxy names private fields it refused to save (caller is not HR).
const droppedWarning = (j) => (Array.isArray(j?.privateDropped) && j.privateDropped.length)
  ? `Saved, but HR-only fields were not changed (${j.privateDropped.join(', ')}). Ask an admin to update them.`
  : '';

export const useStaffStore = create((set, getState) => ({
  _listenerActive: false,
  staffList: [],
  storeError: null,
  // Personal/HR fields (Aadhaar, bank, address, birthday…) live in staffPrivate and
  // are merged in only for HR roles. Everyone else sees blanks and must never send
  // those fields back, or a save would wipe them.
  canReadPrivate: false,

  initData: () => {
    if (getState()._listenerActive) return;
    set({ _listenerActive: true, storeError: null });

    try {
      // Listen to BOTH staff and users/agents nodes and merge them
      const staffRef = ref(db, 'staff');
      const usersRef = ref(db, 'users');

      let staffData = {};
      let usersData = {};
      let privateData = { users: {}, staff: {} };
      let privateListening = false;

      const listenPrivateIfHr = () => {
        const myRole = usersData[auth.currentUser?.uid]?.role;
        if (privateListening || !isHrRole(myRole)) return;
        privateListening = true;
        onValue(ref(db, 'staffPrivate'), (snapshot) => {
          const v = snapshot.val() || {};
          privateData = { users: v.users || {}, staff: v.staff || {} };
          set({ canReadPrivate: true });
          mergeAndUpdate();
        }, (error) => {
          console.error('Staff private sync failed:', error);
          set({ canReadPrivate: false });
        });
      };

      const mergeAndUpdate = () => {
        const merged = new Map();

        // Add staff records first (primary source)
        Object.entries(staffData).forEach(([id, val]) => {
          // Access lives on the LOGIN (users/{uid}) — that is what the sidebar and the
          // server enforce. The staff record's own copy can be stale or missing, and the
          // edit form used to show it and write it back on any save (even a PIN change),
          // silently widening an agent's modules.
          const login = val.linkedUserId ? usersData[val.linkedUserId] : null;
          const access = login ? {
            appRole: login.role || val.appRole,
            allowedModules: Array.isArray(login.allowedModules) ? login.allowedModules : [],
            allowedModulesSavedAt: login.allowedModulesSavedAt,
            permissions: login.permissions || {},
          } : {};
          merged.set(id, { id, source: 'staff', ...val, ...(privateData.staff[id] || {}), ...access });
        });

        // Add users that have agent/staff roles and aren't already in staff
        Object.entries(usersData).forEach(([id, val]) => {
          // Skip if already in merged by same key OR if a staff record is already linked to this user
          const alreadyLinked = Array.from(merged.values()).some(s => s.linkedUserId === id);
          if (!merged.has(id) && !alreadyLinked && val.role && val.role !== 'customer') {
            // Map users fields to staff fields. Spread `val` FIRST so every stored
            // field (joiningDate, supervisorId, etimeEmpCode, …) is carried through,
            // plus the HR-only ones from staffPrivate (pay, bank…); the explicit mappings below then
            // override the few fields that have different names on the users node.
            const priv = privateData.users[id] || {};
            merged.set(id, {
              ...val,
              ...priv,
              id,
              source: 'users',
              fullName: val.name || val.fullName || val.email || 'Unknown',
              mobile: val.phone || val.mobile || '',
              emailAddress: val.email || '',
              appRole: val.role || 'agent',
              employmentStatus: val.isActive === false ? 'Terminated' : (val.accountStatus === 'active' ? 'Active' : 'Active'),
              department: val.department || '',
              designation: val.designation || '',
              avatarUrl: val.avatarUrl || val.photoURL || '',
              // Personal/HR fields come from staffPrivate (HR roles only); Settings
              // stores a login's own date of birth as `dob`.
              birthday: priv.birthday || priv.dob || '', address: priv.address || '',
              bloodGroup: priv.bloodGroup || '', educationQualification: val.educationQualification || '',
              emergencyContact1: priv.emergencyContact1 || '', emergencyContact2: priv.emergencyContact2 || '',
              employeeId: val.employeeId || '',
              linkedUserId: id,
              permissions: val.permissions || {},
              allowedModules: val.allowedModules || [],
              createdAt: val.createdAt || 0,
              updatedAt: val.updatedAt || 0,
            });
          }
        });

        const list = Array.from(merged.values());
        list.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
        console.log('[StaffStore] Merged data, total entries:', list.length, '(staff:', Object.keys(staffData).length, ', users:', Object.keys(usersData).length, ')');
        set({ staffList: list });
      };

      onValue(staffRef, (snapshot) => {
        staffData = snapshot.val() || {};
        mergeAndUpdate();
      }, (error) => {
        console.error('Staff node sync failed:', error);
        set({ storeError: 'Failed to load staff data: ' + error.message });
      });

      onValue(usersRef, (snapshot) => {
        usersData = snapshot.val() || {};
        listenPrivateIfHr();
        mergeAndUpdate();
      }, (error) => {
        console.error('Users node sync failed:', error);
        // Don't block - staff node might still work
      });

    } catch (error) {
      console.error('Failed to initialize Staff store:', error);
      set({ _listenerActive: false, storeError: 'Failed to initialize: ' + error.message });
    }
  },

  addStaff: async (staffData) => {
    try {
      const newId = Date.now().toString() + Math.random().toString(36).substr(2, 5);
      let linkedUserId = staffData.linkedUserId || null;
      let userRecord = null;

      // If login access is requested, provision Firebase Auth user via secondary app
      if (staffData.hasLoginAccess && staffData.pin) {
        const pseudoEmail = `${staffData.mobile}@support.local`;
        try {
          const user = await createAgentAccount(pseudoEmail, staffData.pin);
          linkedUserId = user.uid;
        } catch (authErr) {
          if (authErr.code === 'auth/email-already-in-use') {
            // The number may simply be left over from a staff member who was deleted:
            // a delete only removes the Auth account when the linked uid was known, so
            // an older record can leave the login orphaned. Ask the server what the
            // number is actually attached to before refusing.
            const status = await fetch(`${PROXY_BASE}/api/staff/mobile-status`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
              body: JSON.stringify({ mobile: staffData.mobile }),
            }).then(r => r.json()).catch(() => null);

            if (!status?.canReuse) {
              const who = status?.liveLogin?.name || status?.staffRecord?.name;
              const role = status?.liveLogin?.role || status?.staffRecord?.role;
              throw new Error(
                who
                  ? `The mobile number ${staffData.mobile} is already registered to ${who}${role ? ` (${role})` : ''}. Delete that staff record first, then add this one.`
                  : `The mobile number ${staffData.mobile} is already registered.`,
              );
            }

            // Orphaned login — confirm with the user before taking it over.
            const ok = window.confirm(
              `The mobile number ${staffData.mobile} already has a login, but no staff record points at it ` +
              `(the previous staff member was deleted).\n\nRe-use this number for ${staffData.fullName}? ` +
              `The old login's PIN will be replaced by the new one.`,
            );
            if (!ok) throw new Error(`The mobile number ${staffData.mobile} is already registered.`);

            const reclaimed = await fetch(`${PROXY_BASE}/api/staff/reclaim-login`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
              body: JSON.stringify({ mobile: staffData.mobile, pin: staffData.pin }),
            }).then(r => r.json());
            if (!reclaimed?.ok || !reclaimed.uid) {
              throw new Error(reclaimed?.error || 'Could not re-use the existing login for this number.');
            }
            linkedUserId = reclaimed.uid;
          } else {
            throw new Error('Failed to provision login credentials: ' + authErr.message);
          }
        }

        // Login user record (written server-side, below)
        userRecord = {
          id: linkedUserId,
          name: staffData.fullName,
          email: pseudoEmail,
          role: staffData.appRole || 'agent',
          isActive: true,
          accountStatus: 'active',
          mustResetPin: true,
          failedLoginAttempts: 0,
          allowedModules: staffData.allowedModules || [],
          permissions: staffData.permissions || {},
          createdAt: Date.now()
        };
      }

      // Don't store pin in the staff record
      const { pin, ...staffWithoutPin } = staffData;

      const newStaff = {
        ...staffWithoutPin,
        id: newId,
        linkedUserId,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };

      // Remove undefined values (and HR-only fields this login may not set)
      const cleanStaff = Object.fromEntries(
        Object.entries(getState().canReadPrivate ? newStaff : withoutPrivate(newStaff)).filter(([, v]) => v !== undefined)
      );

      // Write staff + login-user records via the proxy (admin SDK). Direct client
      // RTDB writes silently drop in some browsers; the proxy also enforces the
      // role-assignment matrix server-side.
      const resp = await fetch(`${PROXY_BASE}/api/staff/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ newId, staff: cleanStaff, linkedUserId, userRecord, privateAware: true }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok || !j.ok) {
        throw new Error(j.error || 'Staff record was not saved. Please check your role or contact an admin.');
      }

      console.log('[StaffStore] Staff added via proxy:', newId);
      return { warning: droppedWarning(j) };
    } catch (e) {
      console.error('Failed to add staff:', e);
      throw e;
    }
  },

  updateStaff: async (id, updates) => {
    try {
      let current = (getState().staffList || []).find(s => s.id === id);
      if (!current) {
        // Not in the in-memory list — read the actual node. A staff member can
        // live under `staff/{id}` OR (for real login users) under `users/{id}`.
        const staffSnap = await get(ref(db, `staff/${id}`));
        if (staffSnap.exists()) {
          current = { id, source: 'staff', ...staffSnap.val() };
        } else {
          const userSnap = await get(ref(db, `users/${id}`));
          if (userSnap.exists()) {
            const u = userSnap.val();
            current = { id, source: 'users', linkedUserId: id, fullName: u.name || u.fullName, appRole: u.role, ...u };
          } else {
            throw new Error('Staff not found in database.');
          }
        }
      }

      // A login that cannot read HR-only fields sees them blank; sending those blanks
      // would erase the real values, so they are never sent.
      const canReadPrivate = getState().canReadPrivate;
      if (!canReadPrivate) { updates = withoutPrivate(updates); current = withoutPrivate(current); }

      const { pin, ...updatesWithoutPin } = updates;
      let linkedUserId = current.linkedUserId;
      let firstTimeUserRecord = null;   // full users record when login is granted for the first time
      let pinToReset = null;            // PIN reset for an EXISTING linked user

      // Granting login for the FIRST time → create the Auth account (client secondary app),
      // then hand the full user record to the proxy to persist.
      if (updates.hasLoginAccess && pin && !linkedUserId) {
        const pseudoEmail = `${updates.mobile || current.mobile}@support.local`;
        try {
          const user = await createAgentAccount(pseudoEmail, pin);
          linkedUserId = user.uid;
        } catch (authErr) {
          if (authErr.code === 'auth/email-already-in-use') throw new Error('This mobile number is already registered for login access.');
          throw new Error('Failed to provision credentials: ' + authErr.message);
        }
        firstTimeUserRecord = {
          id: linkedUserId, name: updates.fullName || current.fullName, email: pseudoEmail,
          role: updates.appRole || 'agent', isActive: true, accountStatus: 'active', mustResetPin: true,
          failedLoginAttempts: 0, allowedModules: updates.allowedModules || current.allowedModules || [], allowedModulesSavedAt: Date.now(),
          permissions: updates.permissions || current.permissions || {}, createdAt: Date.now(),
        };
      } else if (pin && linkedUserId) {
        // Existing member → reset their Firebase Auth password (only the admin SDK can).
        pinToReset = pin;
      }

      const updatedStaff = { ...current, ...updatesWithoutPin, linkedUserId, updatedAt: Date.now() };
      const cleanStaff = Object.fromEntries(Object.entries(updatedStaff).filter(([, v]) => v !== undefined));

      // Build the users-node patch (never demote a super_admin via a staff edit).
      let userUpdates = firstTimeUserRecord;
      let authUid = linkedUserId;
      if (!firstTimeUserRecord) {
        if (current.source === 'users') {
          authUid = id;
          userUpdates = {};
          if (updates.fullName !== undefined) userUpdates.name = updates.fullName;
          if (updates.appRole !== undefined && updates.appRole !== current.appRole && current.appRole !== 'super_admin') userUpdates.role = updates.appRole;
          // Stamp WHEN the checklist was saved. moduleAllowed uses it to tell a module
          // an admin actually declined from one that did not exist yet.
          if (updates.allowedModules !== undefined) { userUpdates.allowedModules = updates.allowedModules; userUpdates.allowedModulesSavedAt = Date.now(); }
          if (updates.permissions !== undefined) userUpdates.permissions = updates.permissions;
          if (updates.hasLoginAccess === false) userUpdates.isActive = false;
          if (updates.avatarUrl !== undefined) { userUpdates.avatarUrl = updates.avatarUrl; userUpdates.photoURL = updates.avatarUrl; }
          // Persist EVERY other edited field onto the users node (joiningDate,
          // supervisorId, etimeEmpCode, …; the proxy moves HR-only ones to staffPrivate). An allowlist silently dropped
          // any field not listed — this future-proofs it. Specially-handled keys are
          // skipped here because they're written above (or are sensitive).
          const SPECIAL = new Set(['fullName', 'appRole', 'allowedModules', 'permissions', 'hasLoginAccess', 'avatarUrl', 'pin', 'linkedUserId']);
          for (const [k, v] of Object.entries(updatesWithoutPin)) {
            if (SPECIAL.has(k) || v === undefined) continue;
            userUpdates[k] = v;
          }
          userUpdates.updatedAt = Date.now();
        } else if (linkedUserId) {
          userUpdates = {};
          if (updates.hasLoginAccess === false) { userUpdates.isActive = false; userUpdates.role = 'agent'; }
          else {
            if (updates.appRole !== undefined && updates.appRole !== current.appRole && current.appRole !== 'super_admin') userUpdates.role = updates.appRole;
            if (updates.allowedModules !== undefined) { userUpdates.allowedModules = updates.allowedModules; userUpdates.allowedModulesSavedAt = Date.now(); }
            if (updates.permissions !== undefined) userUpdates.permissions = updates.permissions;
            if (updates.fullName !== undefined) userUpdates.name = updates.fullName;
            if (updates.avatarUrl !== undefined) { userUpdates.avatarUrl = updates.avatarUrl; userUpdates.photoURL = updates.avatarUrl; }
          }
        }
      }

      // One authoritative write through the proxy (admin SDK) — persists staff + user
      // records AND resets the PIN. Direct client writes silently drop in some browsers.
      const resp = await fetch(`${PROXY_BASE}/api/staff/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({
          staffId: id, source: current.source || 'staff', privateAware: true,
          staffData: current.source === 'users' ? null : cleanStaff,
          authUid, userUpdates, resetPin: pinToReset,
        }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok || !j.ok) throw new Error(j.error || 'Could not update staff. Please check your role or contact an admin.');
      return { warning: droppedWarning(j) };
    } catch (e) {
      console.error('Failed to update staff:', e);
      throw e;
    }
  },

  // Sign a staff member out of every device now (leaked PIN / lost phone). Revokes their
  // tokens server-side; `disable` also locks the login until the PIN is reset.
  signOutEverywhere: async (uid, disable = false) => {
    const resp = await fetch(`${PROXY_BASE}/api/staff/${encodeURIComponent(uid)}/signout-all`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify({ disable }),
    });
    const j = await resp.json().catch(() => ({}));
    if (!resp.ok || !j.ok) throw new Error(j.error || 'Could not sign the user out. Check your role.');
    return j;
  },

  deleteStaff: async (id) => {
    try {
      const staff = getState().staffList.find(s => s.id === id);
      // Route through the proxy (admin SDK). Direct client removes silently drop in
      // the browser, and a linked login left behind reappears from the users node —
      // so the proxy removes the staff record AND its linked users/Auth account.
      const resp = await fetch(`${PROXY_BASE}/api/staff/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({
          staffId: id,
          source: staff?.source || 'staff',
          authUid: staff?.linkedUserId || null,
        }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok || !j.ok) {
        throw new Error(j.error || 'Could not delete staff. Please check your role or contact an admin.');
      }
    } catch (e) {
      console.error('Failed to delete staff:', e);
      throw e;
    }
  }
}));
